package com.gsb.vyapar.bhagya

import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.toComposeImageBitmap
import com.gsb.vyapar.bhagya.domain.Location
import com.gsb.vyapar.bhagya.interactor.util.showSuccess
import com.gsb.vyapar.bhagya.util.BuildConfig
import com.gsb.vyapar.bhagya.util.PlatformType
import kotlinx.browser.window
import kotlinx.browser.document
import com.gsb.vyapar.bhagya.interactor.util.hideLoading
import com.gsb.vyapar.bhagya.interactor.util.showError
import org.jetbrains.skia.Image
import kotlin.js.Promise
import kotlin.js.json
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLDialogElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLOptionElement
import org.w3c.files.FileReader
import org.w3c.dom.events.Event

actual fun ByteArray.toComposeImageBitmap(): ImageBitmap {
    return try {
        Image.makeFromEncoded(this).toComposeImageBitmap()
    } catch (e: Throwable) {
        ImageBitmap(1, 1)
    }
}

actual fun share(text: String) {
    val shareData = json(
        "title" to "Share",
        "text" to text,
        "url" to window.location.href
    )
    if (js("navigator.share !== undefined")) {
        val promise: Promise<Unit> = js("navigator.share").unsafeCast<(dynamic) -> Promise<Unit>>()(shareData)
        promise.catch { console.log("Sharing failed: $it") }
    } else {
        window.navigator.clipboard.writeText(text)
        showSuccess("Text copied to clipboard. Share it manually!")
    }
}

actual suspend fun getPhoneNumber(): String? = null

actual suspend fun getOtp(): String? = null

actual fun getAppHash(): String? = null

actual suspend fun captureImage(): ByteArray? = suspendCancellableCoroutine { cont ->
    val isAndroid = BuildConfig.userAgent() == PlatformType.ANDROID
    val isIos = BuildConfig.userAgent() == PlatformType.IOS
    if (!isAndroid && !isIos && js("navigator.mediaDevices && navigator.mediaDevices.getUserMedia") != undefined) {
        // Ensure app-level loading dialog doesn't cover the camera overlay
        try { hideLoading() } catch (_: Throwable) {}
        val video = document.createElement("video") as HTMLVideoElement
        // Help autoplay policies and inline rendering
        video.autoplay = true
        video.muted = true
        video.setAttribute("playsinline", "true")
        video.style.maxWidth = "100%"
        video.style.height = "auto"
        video.style.maxHeight = "60vh"
        video.style.objectFit = "contain"
        val captureBtn = document.createElement("button") as HTMLButtonElement
        val cancelBtn = document.createElement("button") as HTMLButtonElement
        captureBtn.textContent = "Capture"
        cancelBtn.textContent = "Cancel"

        // Apply styling similar to GVBButtonESmall from Compose
        fun styleActionButton(btn: HTMLButtonElement) {
            btn.style.width = "80px"
            btn.style.height = "48px"
            btn.style.borderRadius = "25px"
            btn.style.backgroundColor = "#ff9933"
            btn.style.color = "#ffffff"
            btn.style.border = "none"
            btn.style.fontWeight = "600"
            btn.style.cursor = "pointer"
        }
        styleActionButton(captureBtn)
        styleActionButton(cancelBtn)

        var cameraSelect: HTMLSelectElement? = null
        if (isIos) {
            cameraSelect = document.createElement("select") as HTMLSelectElement
            val backOption = document.createElement("option") as HTMLOptionElement
            backOption.value = "environment"
            backOption.textContent = "Back"
            val frontOption = document.createElement("option") as HTMLOptionElement
            frontOption.value = "user"
            frontOption.textContent = "Front"
            cameraSelect.appendChild(backOption)
            cameraSelect.appendChild(frontOption)
            cameraSelect.value = "environment"
            cameraSelect.style.marginTop = "16px"
            cameraSelect.style.marginBottom = "16px"
            cameraSelect.style.width = "200px"
            cameraSelect.style.height = "36px"
            cameraSelect.style.border = "1px solid #cccccc"
            cameraSelect.style.borderRadius = "4px"
            cameraSelect.style.padding = "4px"
        }

        // Build an overlay that prefers <dialog> top-layer, with fallback to a fixed <div>
        val supportsDialog = js("typeof HTMLDialogElement !== 'undefined'") as Boolean
        var usedDialog = false
        val overlayDialog: HTMLDialogElement? = if (supportsDialog) {
            usedDialog = true
            (document.createElement("dialog") as HTMLDialogElement).apply {
                style.margin = "0"
                style.padding = "0"
                style.border = "none"
                style.backgroundColor = "transparent"
                style.width = "100vw"
                style.height = "100vh"
                style.display = "flex"
                style.alignItems = "center"
                style.justifyContent = "center"
            }
        } else null
        val overlayDiv: HTMLDivElement? = if (!supportsDialog) {
            usedDialog = false
            (document.createElement("div") as HTMLDivElement).apply {
                style.position = "fixed"
                style.left = "0"
                style.top = "0"
                style.width = "100%"
                style.height = "100%"
                style.zIndex = "2147483647"
                style.setProperty("pointer-events", "auto")
                style.display = "flex"
                style.alignItems = "center"
                style.justifyContent = "center"
                style.backgroundColor = "transparent"
            }
        } else null
        val overlay = (overlayDialog ?: overlayDiv!!) // Element we will append to

        // Content wrapper that provides the dark backdrop and layout
        val content = document.createElement("div") as HTMLDivElement
        content.style.position = "relative"
        content.style.width = "100%"
        content.style.height = "100%"
        content.style.backgroundColor = "rgba(0,0,0,0.8)"
        content.style.display = "flex"
        content.style.flexDirection = "column"
        content.style.alignItems = "center"
        content.style.justifyContent = "center"
        content.style.paddingTop = "16px"
        content.style.paddingBottom = "16px"
        content.style.boxSizing = "border-box"
        content.style.overflowY = "auto"
        // Keep above app content when using <div> fallback
        overlayDiv?.style?.zIndex = "2147483647"
        val btnContainer = document.createElement("div") as HTMLDivElement
        btnContainer.style.display = "flex"
        btnContainer.style.flexDirection = "row"
        btnContainer.style.columnGap = "16px"
        btnContainer.style.marginTop = "16px"
        btnContainer.appendChild(cancelBtn)
        btnContainer.appendChild(captureBtn)

        content.appendChild(video)
        if (cameraSelect != null) {
            content.appendChild(cameraSelect)
        }
        content.appendChild(btnContainer)
        overlay.appendChild(content)
        // Prefer attaching inside Compose's shadow root (if present) so we share stacking context
        val host: dynamic = (document.body?.asDynamic()?.shadowRoot) ?: document.body
        host.appendChild(overlay)
        if (usedDialog) {
            try {
                overlayDialog?.showModal()
            } catch (_: Throwable) {
                // If showModal fails, keep using <dialog> but emulate fixed overlay
                usedDialog = false
                try { overlayDialog?.open = true } catch (_: Throwable) {}
                overlayDialog?.style?.apply {
                    position = "fixed"
                    left = "0"
                    top = "0"
                    width = "100%"
                    height = "100%"
                    zIndex = "2147483647"
                    display = "flex"
                }
            }
        }

        val mediaDevices = window.navigator.asDynamic().mediaDevices
        var currentStream: dynamic = null

        fun startStream(facing: String) {
            currentStream?.getTracks()?.forEach { track -> track.stop() }
            val constraints: dynamic = js("{}")
            constraints.video = js("{}")
            // Prefer facing mode but don't make it mandatory (avoid overconstrained errors)
            constraints.video.facingMode = js("{}")
            constraints.video.facingMode.ideal = facing
            console.log("Requesting camera with", constraints)
            mediaDevices.getUserMedia(constraints)
                .then { stream: dynamic ->
                    currentStream = stream
                    video.asDynamic().srcObject = stream
                    val startPlayback = { try { video.play() } catch (_: dynamic) {} }
                    // Start rendering when metadata is ready; also try immediately
                    video.onloadedmetadata = { _ -> startPlayback() }
                    startPlayback()
                }
                .catch { _: dynamic ->
                    // Fallback without constraints
                    val fallback: dynamic = js("{}")
                    fallback.video = true
                    console.warn("Primary getUserMedia failed, trying fallback")
                    mediaDevices.getUserMedia(fallback)
                        .then { stream: dynamic ->
                            currentStream = stream
                            video.asDynamic().srcObject = stream
                            val startPlayback = { try { video.play() } catch (_: dynamic) {} }
                            video.onloadedmetadata = { _ -> startPlayback() }
                            startPlayback()
                        }
                        .catch { err: dynamic ->
                            console.error("getUserMedia failed:", err)
                            currentStream = null
                            try { showError("Unable to access camera. Check permissions and try again.") } catch (_: dynamic) {}
                        }
                }
        }

        val initialFacing = cameraSelect?.value ?: "environment"
        startStream(initialFacing)
        cameraSelect?.onchange = { _ ->
            startStream(cameraSelect!!.value)
        }

        fun finish(bytes: ByteArray?) {
            currentStream?.getTracks()?.forEach { track -> track.stop() }
            if (usedDialog) { try { overlayDialog?.close() } catch (_: Throwable) {} }
            try { overlay.remove() } catch (_: Throwable) {}
            cont.resume(bytes)
        }

        // Enable capture only when video has dimensions
        captureBtn.disabled = true
        video.onloadeddata = { _ ->
            captureBtn.disabled = (video.videoWidth <= 0 || video.videoHeight <= 0)
        }

        cancelBtn.onclick = { finish(null) }
        captureBtn.onclick = {
            val canvas = document.createElement("canvas") as HTMLCanvasElement
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
            ctx.drawImage(video, 0.0, 0.0)
            canvas.toBlob({ blob ->
                if (blob == null) {
                    finish(null)
                } else {
                    val reader = FileReader()
                    reader.onloadend = {
                        val buffer = reader.result as ArrayBuffer
                        val int8 = Int8Array(buffer)
                        val bytes = int8.unsafeCast<ByteArray>()
                        finish(bytes)
                    }
                    reader.onerror = { finish(null) }
                    reader.readAsArrayBuffer(blob)
                }
            })
        }

    } else {
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = "image/*"
        input.setAttribute("capture", "environment")
        input.style.display = "none"
        document.body?.appendChild(input)   // keep input in the DOM

        var pickerActive = false
        var cleanedUp = false

        var blurHandler: (Event) -> Unit = {}
        var focusHandler: (Event) -> Unit = {}
        var visHandler: (Event) -> Unit = {}

        fun cleanup() {
            if (cleanedUp) return
            cleanedUp = true
            // Remove listeners and input element
            input.onchange = null
            try { window.removeEventListener("blur", blurHandler) } catch (_: Throwable) {}
            try { window.removeEventListener("focus", focusHandler) } catch (_: Throwable) {}
            try { document.removeEventListener("visibilitychange", visHandler) } catch (_: Throwable) {}
            document.body?.removeChild(input)
        }

        fun checkCancel() {
            // Delay slightly to allow a real change event to come first
            window.setTimeout({
                val filesLen = input.files?.length ?: 0
                if (filesLen == 0) {
                    if (cont.isActive) cont.resume(null)
                    cleanup()
                }
            }, 150)
        }

        blurHandler = { _ -> pickerActive = true }
        focusHandler = { _ -> if (pickerActive) checkCancel() }
        visHandler = { _ ->
            val state = js("document.visibilityState") as String
            if (state == "visible" && pickerActive) checkCancel()
        }

        // Best-effort: some browsers may emit a non-standard 'cancel'
        input.addEventListener("cancel", { _ ->
            if (cont.isActive) cont.resume(null)
            cleanup()
        })

        window.addEventListener("blur", blurHandler)
        window.addEventListener("focus", focusHandler)
        document.addEventListener("visibilitychange", visHandler)

        // Ensure resources are cleaned if coroutine is cancelled
        cont.invokeOnCancellation { cleanup() }

        input.onchange = {
            val file = input.files?.item(0)
            if (file == null) {
                if (cont.isActive) cont.resume(null)
                cleanup()
            } else {
                val reader = FileReader()
                reader.onloadend = {
                    val arrayBuffer = reader.result as ArrayBuffer
                    val int8 = Int8Array(arrayBuffer)
                    val bytes = int8.unsafeCast<ByteArray>()
                    if (cont.isActive) cont.resume(bytes)
                    cleanup()
                }
                reader.onerror = {
                    if (cont.isActive) cont.resume(null)
                    cleanup()
                }
                reader.onabort = {
                    if (cont.isActive) cont.resume(null)
                    cleanup()
                }
                reader.readAsArrayBuffer(file)
            }
        }

        input.click()
    }
}

actual suspend fun requestDeviceLocation(): Location? = suspendCancellableCoroutine { cont ->
    val navigator = window.navigator.asDynamic()
    val geo = navigator.geolocation
    if (geo != undefined) {
        geo.getCurrentPosition({ pos: dynamic ->
            val coords = pos.coords
            cont.resume(Location(latitude = coords.latitude as Double, longitude = coords.longitude as Double))
        }, { _ -> cont.resume(null) })
    } else {
        cont.resume(null)
    }
}
